import { createContext, useContext, type ReactNode } from 'react'

/**
 * Whether this part of the app names the API call behind what it shows.
 * Only a signed-in SeeWhoCares admin sees those: the paths are a
 * developer's view of the product, like the API calls drawer, and mean
 * nothing to an operator, a facility administrator, a worker or a sales
 * login. Off unless the admin shell switches it on.
 */
const EndpointHintsContext = createContext(false)

export const EndpointHintsProvider = EndpointHintsContext.Provider

/** True where the reader is a SeeWhoCares admin, for wording that names an endpoint in passing. */
export function useEndpointHints(): boolean {
  return useContext(EndpointHintsContext)
}

/** One look for every path, whatever the heading it sits in: small, light and quiet. */
const HINT_CLASS = 'font-mono text-[10px] font-normal normal-case leading-tight tracking-normal text-ink-mid/60'

/**
 * The API call behind a section, beside its heading: `GET /facilities/{facilityId}/barriers`.
 * Renders nothing outside the admin shell, so a component shared with the
 * operator and worker apps can carry its hint without showing it to them.
 * It owns its size and colour, so `className` is for placement only (a margin, `block`).
 */
export function EndpointHint({ children, className = '' }: { children: ReactNode; className?: string }) {
  if (!useEndpointHints()) {
    return null
  }
  return <span className={`${HINT_CLASS} ${className}`.trim()}>{children}</span>
}
